// A question and its answer, on a surface that asks nothing — the read-back a
// picker becomes on a submitted order.
//
// A native <select> has no `readOnly`, so the only way to keep its answer
// reachable is to stop rendering a control: a disabled <select> is out of the tab
// order, and forms-mode navigation skips it along with the answer it was holding
// (ADR-018 phase 13). This is that rendering, in ONE place — the saved-kit picker
// and every per-row picker in the kit contents list share it, so a change to how
// a locked answer reads lands on all of them at once.
//
// The label is a <span>, not a <label for>: static text is not a labelable
// element, so a `for` here would point at nothing. `.field-readback__label`
// carries the label styling its editable twin gets from `.form-row > label`.
//
// `id` is the field key the control it replaces carried, keeping the
// key-equals-id contract KittingFields' header comment states for the whole
// Assembly form: the DOM still says which question this answer belongs to, and it
// is a scroll target. Not a FOCUS target — a bare <p> ignores `.focus()` — which
// costs nothing today, because the missing-field summary that reveals a key is
// rendered only on the editable mount, where the control is still a control.

// Nothing was answered. Same em-dash the radio-group read-back and the derived
// count use, rather than an empty box that reads as a field still to fill.
const BLANK = '—';

// For the fields that stay INPUTS on a read-only mount — a text answer keeps its
// value in the DOM, so it only needs `readOnly` plus `input--locked` styling, not
// a different element. Exported so the em-dash has one author across both
// renderings: a blank locked input and a blank read-back must not disagree about
// what "unanswered" looks like.
export function readBackValue(value: string | undefined): string {
  return value !== undefined && value.trim() !== '' ? value : BLANK;
}

export function FieldReadBack({
  id,
  label,
  value,
}: {
  id?: string;
  label: string;
  value: string | undefined;
}): JSX.Element {
  return (
    <div className="form-row">
      <span className="field-readback__label">{label}</span>
      <p className="value--locked" {...(id !== undefined ? { id } : {})}>
        {readBackValue(value)}
      </p>
    </div>
  );
}
